import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLang } from '../context/LangContext';
import api from '../utils/api';
import './Conversations.css';

export default function Conversations() {
  const { lang } = useLang();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [pausedChatIds, setPausedChatIds] = useState(() => new Set());
  const messagesEnd = useRef(null);

  const fetchChats = useCallback(() => {
    return api.get('/rt/chats')
      .then(r => { if (r.data?.success) setChats(r.data.data || []); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchChats().finally(() => setLoading(false));
  }, [fetchChats]);

  const loadConvo = async (chat) => {
    setSelected(chat);
    setMsgLoading(true);
    setMessages([]);
    try {
      const res = await api.post('/rt/conversation', { chatId: chat.chat_id });
      if (res.data?.success) {
        setMessages(res.data.data || []);
        setChats(prev =>
          prev.map(c =>
            c.chat_id === chat.chat_id ? { ...c, unread: 0 } : c,
          ),
        );
        setTimeout(() => messagesEnd.current?.scrollIntoView({ behavior: 'smooth' }), 100);
      }
    } catch { /* ignore */ }
    setMsgLoading(false);
  };

  const openSendConfirm = () => {
    if (!reply.trim() || !selected) return;
    setSendConfirmOpen(true);
  };

  const handleSendConfirmed = async () => {
    if (!reply.trim() || !selected) return;
    setSendConfirmOpen(false);
    setSending(true);
    try {
      const sendRes = await api.post('/rt/send-message', {
        chatId: selected.chat_id,
        text: reply.trim(),
        pauseBot: true,
      });
      if (!sendRes.data?.success) {
        throw new Error(sendRes.data?.msg || 'Send failed');
      }
      setReply('');
      setPausedChatIds((prev) => new Set(prev).add(selected.chat_id));
      await loadConvo(selected);
    } catch { /* ignore */ }
    setSending(false);
  };

  const handleResumeBot = async () => {
    if (!selected) return;
    try {
      const r = await api.post('/rt/resume-bot', { chatId: selected.chat_id });
      if (r.data?.success) {
        setPausedChatIds((prev) => {
          const next = new Set(prev);
          next.delete(selected.chat_id);
          return next;
        });
      }
    } catch { /* ignore */ }
  };

  const filteredChats = chats.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (c.senderName || '').toLowerCase().includes(s) ||
           (c.senderMobile || '').includes(s) ||
           (c.lastText || '').toLowerCase().includes(s) ||
           (c.chat_id || '').includes(s);
  });

  const formatTime = (ts) => {
    if (ts == null || ts === '') return '';
    try {
      const n = Number(ts);
      if (Number.isNaN(n)) return '';
      const ms = n > 1e12 ? n : n * 1000;
      const d = new Date(ms);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  const getMsgText = (msg) => {
    if (!msg?.msgContext) return '';
    let ctx = msg.msgContext;
    if (typeof ctx === 'string') {
      try {
        ctx = JSON.parse(ctx);
      } catch {
        return ctx;
      }
    }
    if (ctx?.text?.body) return ctx.text.body;
    if (ctx?.msgContext?.text?.body) return ctx.msgContext.text.body;
    if (ctx?.type === 'image') return `📷 ${lang === 'en' ? 'Image' : 'Imagen'}`;
    if (ctx?.type === 'video') return '🎥 Video';
    if (ctx?.type === 'audio') return '🎵 Audio';
    if (ctx?.type === 'document') return `📄 ${lang === 'en' ? 'Document' : 'Documento'}`;
    return '';
  };

  return (
    <div className="convo-page">
      <div className="convo-layout">
        {/* Chat List */}
        <div className={`convo-list ${selected ? 'convo-list--hidden-mobile' : ''}`}>
          <div className="convo-list__header">
            <h2>{lang === 'en' ? 'Conversations' : 'Conversaciones'}</h2>
            <span className="convo-list__count">{chats.length}</span>
          </div>
          <div className="convo-list__search">
            <input
              type="search"
              autoComplete="off"
              placeholder={lang === 'en' ? 'Search conversations...' : 'Buscar conversaciones...'}
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>
          {loading ? (
            <div className="convo-list__loading"><div className="spinner" /></div>
          ) : filteredChats.length === 0 ? (
            <div className="convo-list__empty">
              <p>{lang === 'en' ? 'No conversations yet' : 'Sin conversaciones aún'}</p>
            </div>
          ) : (
            <div className="convo-list__items">
              {filteredChats.map(chat => (
                <div
                  key={chat.chat_id}
                  role="button"
                  tabIndex={0}
                  className={`convo-item ${selected?.chat_id === chat.chat_id ? 'convo-item--active' : ''}`}
                  onClick={() => loadConvo(chat)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      loadConvo(chat);
                    }
                  }}
                >
                  <div className="convo-item__avatar">
                    {(chat.senderName || '?')[0].toUpperCase()}
                  </div>
                  <div className="convo-item__info">
                    <div className="convo-item__top">
                      <span className="convo-item__name-wrap">
                        <span className="convo-item__name">{chat.senderName || chat.senderMobile || chat.chat_id}</span>
                        {Number(chat.unread) > 0 && (
                          <span className="convo-item__badge" aria-label="Unread">{chat.unread > 99 ? '99+' : chat.unread}</span>
                        )}
                      </span>
                      <span className="convo-item__time">{formatTime(chat.timestamp)}</span>
                    </div>
                    <p className="convo-item__preview">
                      {chat.lastText
                        ? chat.lastText
                        : (chat.senderMobile || '')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Chat View */}
        <div className={`convo-view ${!selected ? 'convo-view--empty' : ''}`}>
          {!selected ? (
            <div className="convo-view__placeholder">
              <span className="convo-view__placeholder-icon">💬</span>
              <p>{lang === 'en' ? 'Select a conversation to view messages' : 'Selecciona una conversación para ver mensajes'}</p>
            </div>
          ) : (
            <>
              <div className="convo-view__header">
                <button type="button" className="convo-view__back" onClick={() => setSelected(null)} aria-label={lang === 'en' ? 'Back' : 'Volver'}>←</button>
                <div className="convo-view__contact">
                  <strong>{selected.senderName || selected.senderMobile || 'Chat'}</strong>
                  {selected.senderMobile && <span>{selected.senderMobile}</span>}
                </div>
              </div>

              <div className="convo-view__messages">
                {msgLoading ? (
                  <div className="convo-view__msg-loading"><div className="spinner" /></div>
                ) : messages.length === 0 ? (
                  <div className="convo-view__no-msgs">
                    <p>{lang === 'en' ? 'No messages' : 'Sin mensajes'}</p>
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div key={msg.id ?? `${msg.chat_id}-${msg.timestamp}-${msg.senderMobile}`} className={`msg ${msg.route === 'INCOMING' ? 'msg--in' : 'msg--out'}`}>
                      <div className="msg__bubble">
                        {getMsgText(msg)}
                        <span className="msg__time">{formatTime(msg.timestamp)}</span>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEnd} />
              </div>

              {pausedChatIds.has(selected.chat_id) && (
                <div className="convo-bot-pause-banner" role="status">
                  <p>
                    {lang === 'en'
                      ? 'Bot paused for this chat (1 hour). The AI will resume responding after the pause expires.'
                      : 'Bot pausado para este chat (1 hora). La IA volverá a responder cuando termine la pausa.'}
                  </p>
                  <button type="button" className="convo-bot-pause-banner__resume" onClick={handleResumeBot}>
                    {lang === 'en' ? 'Resume Bot' : 'Reanudar bot'}
                  </button>
                </div>
              )}

              <div className="convo-view__input">
                <input
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder={lang === 'en' ? 'Type a message...' : 'Escribe un mensaje...'}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      openSendConfirm();
                    }
                  }}
                />
                <button type="button" onClick={openSendConfirm} disabled={sending || !reply.trim()} aria-label={lang === 'en' ? 'Send' : 'Enviar'}>
                  {sending ? '...' : '➤'}
                </button>
              </div>

              {sendConfirmOpen && (
                <div className="convo-modal-overlay" role="presentation" onClick={() => !sending && setSendConfirmOpen(false)}>
                  <div className="convo-modal" role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
                    <p className="convo-modal__text">
                      {lang === 'en'
                        ? 'Attention: If you reply manually, the AI bot will stop responding to this customer for 1 hour to avoid crossed messages. Continue?'
                        : 'Atención: si respondes manualmente, el bot de IA dejará de responder a este cliente durante 1 hora para evitar mensajes cruzados. ¿Continuar?'}
                    </p>
                    <div className="convo-modal__actions">
                      <button type="button" className="convo-modal__cancel" disabled={sending} onClick={() => setSendConfirmOpen(false)}>
                        {lang === 'en' ? 'Cancel' : 'Cancelar'}
                      </button>
                      <button type="button" className="convo-modal__confirm" disabled={sending} onClick={handleSendConfirmed}>
                        {lang === 'en' ? 'Send & Pause Bot' : 'Enviar y pausar bot'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
